import { toast } from "@/components/common/Toast"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { useUploadKnowledgebaseFile } from "@/services/api/knowledgebaseService"
import { KnowledgebaseFile } from "@/types/knowledgebase"
import { isLimitError } from "@/utils/limitErrors"
import { useCallback, useRef, useState } from "react"

export function useKnowledgebaseUpload(refetchFiles: () => void) {
  const { translate } = useTranslation()
  const [uploadingCount, setUploadingCount] = useState(0)
  const [lastUploadedFiles, setLastUploadedFiles] = useState<
    KnowledgebaseFile[]
  >([])
  const pendingRef = useRef(0)
  const uploadedInBatchRef = useRef<KnowledgebaseFile[]>([])
  // A limit error already got its upgrade toast from the axios interceptor, so
  // only another kind of failure earns the generic batch toast below.
  const hadGenericFailureRef = useRef(false)

  const { mutate } = useUploadKnowledgebaseFile(
    (data) => {
      uploadedInBatchRef.current.push(data)
      pendingRef.current--
      if (pendingRef.current === 0) {
        setUploadingCount(0)
        setLastUploadedFiles(uploadedInBatchRef.current)
        uploadedInBatchRef.current = []
        hadGenericFailureRef.current = false
        refetchFiles()
      }
    },
    (error) => {
      pendingRef.current--
      if (!isLimitError(error)) hadGenericFailureRef.current = true
      if (pendingRef.current === 0) {
        setUploadingCount(0)
        if (uploadedInBatchRef.current.length > 0) {
          setLastUploadedFiles(uploadedInBatchRef.current)
          refetchFiles()
        }
        uploadedInBatchRef.current = []
        if (hadGenericFailureRef.current) {
          toast.error(translate("KNOWLEDGEBASE/TOAST_UPLOAD_FILE_ERROR"))
        }
        hadGenericFailureRef.current = false
      }
    },
    () => {},
  )

  const handleFiles = useCallback(
    (files: File[]) => {
      if (!files.length) return
      uploadedInBatchRef.current = []
      pendingRef.current += files.length
      setUploadingCount((prev) => prev + files.length)
      files.forEach((f) => mutate(f))
    },
    [mutate],
  )

  const clearLastUploaded = useCallback(() => {
    setLastUploadedFiles([])
  }, [])

  return { uploadingCount, handleFiles, lastUploadedFiles, clearLastUploaded }
}
